import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ReactComponent as PlusIcon } from '@pkg/assets/icons/v2/plus.svg';
import {
    GroupsFilters,
    Portal,
    PortalArea,
    YatModalSideSheet,
    YatModalSideSheetSide,
    YatModalSideSheetVariant,
} from '@pkg/components';
import { GroupsFiltersModal } from '@pkg/components/Sections/Groups/GroupFilters';
import { Button, ButtonIcon } from '@pkg/components/Sections/Profile/Buttons';
import { ReactComponent as FilterIcon } from '@pkg/components/Sections/Profile/icons/icon-filter.svg';
import { useAppDispatch, useAppSelector, useMedia } from '@pkg/hooks';

import { type GroupsFilter } from '@app/client/common/structs/groups/groups';
import { globalNavigationActions } from '@app/store/slices';

import {
    Buttons,
    GroupsCompareModalWrapper,
    GroupsCompareWrapper,
    Heading,
    MobileButton,
    SideSheetInner,
    Subheading,
} from './styles';

interface Props {
    selectedFilter?: GroupsFilter;
    filterOptions?: GroupsFilter[];
    onClickFilter?(selected: GroupsFilter): void;
    clearFilter?(): void;
}

export const GroupCompare = ({ selectedFilter, filterOptions, onClickFilter, clearFilter }: Props) => {
    const { t } = useTranslation('groups');
    const theme = useTheme();
    const notMobile = useMedia(`(min-width: ${theme.breakpoints.mobile})`);
    const isRegisteredUser = useAppSelector((state) => state.user.isRegisteredUser);
    const showCompressedLayout = useMedia(`(max-width: 1160px)`);

    const [isOpen, setIsOpen] = useState(false);

    const toggleIsOpen = useCallback(() => {
        setIsOpen((open) => !open);
    }, []);

    const dispatch = useAppDispatch();

    const handleCreatePod = useCallback(() => {
        dispatch(globalNavigationActions.showCreatePodModal(true));
    }, [dispatch]);

    return (
        <GroupsCompareWrapper>
            {notMobile ? (
                <Buttons>
                    {!showCompressedLayout ? (
                        <Button size="medium50" variant="foggy" startIcon={<FilterIcon />} onClick={toggleIsOpen}>
                            {t('pods_compare_cta')}
                        </Button>
                    ) : (
                        <ButtonIcon onClick={toggleIsOpen} size="smallMedium" color="transparent">
                            <FilterIcon />
                        </ButtonIcon>
                    )}

                    {isRegisteredUser ? (
                        <Button size="medium50" variant="highlight" startIcon={<PlusIcon />} onClick={handleCreatePod}>
                            {showCompressedLayout ? 'New Pod' : 'Create a new Pod'}
                        </Button>
                    ) : null}
                </Buttons>
            ) : (
                <MobileButton>
                    <ButtonIcon pulse onClick={toggleIsOpen}>
                        <FilterIcon />
                    </ButtonIcon>
                </MobileButton>
            )}

            {notMobile && isOpen ? (
                <Portal destination={PortalArea.Transition}>
                    <GroupsCompareModalWrapper>
                        <YatModalSideSheet
                            side={YatModalSideSheetSide.right}
                            variant={YatModalSideSheetVariant.roundedGradientSmall}
                            modalLabelId="comparePods"
                            open={isOpen}
                            closeCallback={toggleIsOpen}
                        >
                            <SideSheetInner>
                                <Heading>{t('pods_compare_heading')}</Heading>
                                <Subheading>{t('pods_compare_subheading')}</Subheading>
                                <GroupsFilters />
                            </SideSheetInner>
                        </YatModalSideSheet>
                    </GroupsCompareModalWrapper>
                </Portal>
            ) : null}

            {!notMobile && isOpen ? (
                <GroupsFiltersModal
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    selectedFilter={selectedFilter}
                    filterOptions={filterOptions}
                    onClickFilter={onClickFilter}
                    clearFilter={clearFilter}
                />
            ) : null}
        </GroupsCompareWrapper>
    );
};
